import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { PermissionsContext } from "../contexts/PermissionsContext";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Table } from "rsuite";
import { Sales } from "../models/sales/sales";
import { getSales } from "../services/sales/SalesApi";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
} from "chart.js";
import {
  getDashboardMonthlySales,
  getTopCustomers,
  getTopMerchants,
  getTopProducts,
} from "../services/DashboardApi";
import { Link } from "react-router-dom";
import { money, statusColorMap } from "../utils/helper";

const { Column, HeaderCell, Cell } = Table;
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement
);

interface HomePageProps {}

const HomePage: FC<HomePageProps> = ({}) => {
  const { permissions, setPermissions } = useContext(PermissionsContext);
  const [activeOrders, setActiveOrders] = useState<Sales[]>([]);
  const [mounted, setMounted] = useState(false);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [dataSales, setDataSales] = useState<number[]>([]);
  const [topMerchantLabels, setTopMerchantLabels] = useState<string[]>([]);
  const [topMerchantSales, setTopMerchantSales] = useState<number[]>([]);
  const [topCustomerLabels, setTopCustomerLabels] = useState<string[]>([]);
  const [topCustomerSales, setTopCustomerSales] = useState<number[]>([]);
  const [topProductLabels, setTopProductLabels] = useState<string[]>([]);
  const [topProductSales, setTopProductSales] = useState<number[]>([]);
  const [limit, setLimit] = useState(5);
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    getDashboardMonthlySales(year)
      .then((v) => v.json())
      .then((v) => {
        setDataSales(v.data.map((e: any) => Math.round(e.amount).toFixed(0)));
      });

    getTopMerchants(limit)
      .then((v) => v.json())
      .then((v) => {
        setTopMerchantLabels(v.data.map((e: any) => e.name));
      });
    getTopMerchants(limit)
      .then((v) => v.json())
      .then((v) => {
        setTopMerchantLabels(v.data.map((e: any) => e.name));
        setTopMerchantSales(v.data.map((e: any) => e.total));
      });
    getTopCustomers(limit)
      .then((v) => v.json())
      .then((v) => {
        setTopCustomerLabels(v.data.map((e: any) => e.full_name));
        setTopCustomerSales(v.data.map((e: any) => e.total));
      });
    getTopProducts(limit)
      .then((v) => v.json())
      .then((v) => {
        setTopProductLabels(v.data.map((e: any) => e.name));
        setTopProductSales(v.data.map((e: any) => e.quantity));
      });
    getSales({
      page: 1,
      size: 5,
      status: "PENDING",
    })
      .then((v) => v.json())
      .then((v) => setActiveOrders(v.data.items));
  }, [mounted, year, limit]);

  const salesData = {
    labels: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
    datasets: [
      {
        label: "Sales",
        data: dataSales,
        backgroundColor: "rgba(75,192,192,0.4)",
        borderColor: "rgba(75,192,192,1)",
        tension: 0.3,
      },
    ],
  };

  const bestSalesData = {
    labels: topProductLabels,
    datasets: [
      {
        data: topProductSales,
        backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#009688", "#2196F3",],
      },
    ],
  };

  const topCustomersData = {
    labels: topCustomerLabels,
    datasets: [
      {
        label: "Customers",
        data: topCustomerSales,
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        borderColor: "rgba(153, 102, 255, 1)",
      },
    ],
  };

  const topMerchantData = {
    labels: topMerchantLabels,
    datasets: [
      {
        label: "Merchant",
        data: topMerchantSales,
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        borderColor: "rgba(54, 162, 235, 1)",
      },
    ],
  };

  return (
    <MainLayout noCard>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-md col-span-2">
          <h2 className="text-xl font-bold">Sales</h2>
          <Line data={salesData} height={120} />
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-xl font-bold">Best Sales Product</h2>
          <Doughnut data={bestSalesData} height={120} />
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-xl font-bold">Top Customers</h2>
          <Bar data={topCustomersData} height={160} />
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-xl font-bold">Top Merchant</h2>
          <Bar data={topMerchantData} height={160} />
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md">
          <h2 className="text-xl font-bold">Recent Orders</h2>
          <Table height={240} data={activeOrders}>
            <Column width={100}>
              <HeaderCell>Order ID</HeaderCell>
              <Cell  >
                {(e: any) => (
                  <Link className=" hover:underline hover:font-semibold" to={`/sales/${e.id}`}>{e.sales_number}</Link>
                )}
                </Cell>
            </Column>
            <Column>
              <HeaderCell>Customer</HeaderCell>
              <Cell dataKey="data_contact.full_name" />
            </Column>
            <Column>
              <HeaderCell>Merchant</HeaderCell>
              <Cell dataKey="merchant.name" />
            </Column>
            <Column>
              <HeaderCell align="right">Total</HeaderCell>
              <Cell align="right" >
                {(e: any) => (
                  <span>{money(e.total)}</span>
                )}
                </Cell>
            </Column>
            <Column>
              <HeaderCell align="center">Status</HeaderCell>
              <Cell align="center" >
                {(e: any) => {
                  const statusColor = statusColorMap[e.status as keyof typeof statusColorMap] ?? "";
                  return (
                    <span className={`text-white text-xs text-center ${statusColor} py-1 px-2 rounded-lg`}>{e.status}</span>
                  );
                }}
                </Cell>
            </Column>
          </Table>
        </div>
      </div>
    </MainLayout>
  );
};
export default HomePage;
